import {getId, getType} from 'apis/model/base'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useMemo} from 'react'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import OrganizationContainerContext from '../OrganizationContainerContext'
import {useHistory} from "../../../../modules/navigation/useRouter";
import {LayoutContext} from "../../../../components/layouts/Default/LayoutContext";
import renderPagingLoading from "../../../MainPage/functions/renderPagingLoading";
import CustomLazyPaginationWrapper from "../../../../modules/asyncCache/components/CustomLazyPaginationWrapper";
import {NullAsDefault} from "../../../Shared";
import {organization_getGroups_Api, organization_getGroupTree_Api} from "../../../../apis"
import useAsyncWithCache from 'modules/asyncCache/useAsyncWithCache';
import getLinkToDetail from "../../../../helpers/getLinkToDetail";
import {Link} from "react-router-dom";
import {HeaderWithIconLine} from "../../Templates/items/ListHeader";
import {scrollToRow} from "../../Missal/functions/scrollToRow";
import {FiChevronsRight} from "react-icons/fi";
import {FaUsers} from "react-icons/fa6";
import _ from "lodash"
import classNames from "classnames";
import {PiUserCircleCheckFill} from "react-icons/pi";
import {WrapperBlockSider} from "../components/WrapperBlockSider";
import {JoinButton} from "../../custom/ToggleActionButton";
import EmptyHolder from "../../../../components/EmptyHolder";
import {Skeleton} from "antd";
import {BsPeopleFill} from "react-icons/bs";
import {formatNumber} from "../../../../helpers/number";

const DeptSkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[1, 2, 3, 4].map(i => (
            <div key={i} className="p-4 border border-color-50 rounded-xl">
                <Skeleton active paragraph={{rows: 2}} title={{width: '60%'}}/>
            </div>
        ))}
    </div>
)

const StatCard = ({icon, label, value, note, color = 'text-indigo-600', bg = 'bg-indigo-50'}) => (
    <div className="flex-1 flex items-center gap-3 p-4 background rounded-xl border border-color-50 shadow-sm">
        <div className={classNames('flex-shrink-0 p-3 rounded-xl', bg)}>
            {React.cloneElement(icon, {size: 22, className: color})}
        </div>
        <div className="flex flex-col">
            <span className="font-bold text-2xl leading-snug">{value}</span>
            <span className="text-xs text-color-400 tracking-wide">{label}</span>
            {note && (
                <span className="text-2xs text-color-300 italic mt-0.5">{note}</span>
            )}
        </div>
    </div>
)

const DeptCard = ({item, t}) => {
    const isJoined = item?.joined_status
    const canJoin = item?.available_actions?.can_join && !isJoined

    return (
        <Link
            to={getLinkToDetail(item)}
            className="no-underline h-full">
            <div className="h-full p-4 border border-color-50 background rounded-xl shadow-sm hover:shadow-md hover:scale-2xs transition-all flex flex-col gap-3">
                {/* Header row */}
                <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 p-2 bg-indigo-50 rounded-lg">
                        <FaUsers size={18} className="text-indigo-500"/>
                    </div>
                    <div className="flex flex-col flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-bold text-base text-color-000 leading-snug">
                                {item?.title}
                            </h3>
                            {item?.active_as_member?.id && (
                                <PiUserCircleCheckFill size={15} className="text-green-500 flex-shrink-0"/>
                            )}
                        </div>
                        {item?.group_title && (
                            <span className="mt-0.5 text-xs text-indigo-500 font-medium tracking-wide">
                                {item?.group_title}
                            </span>
                        )}
                    </div>
                </div>

                {/* Patron saint */}
                {item?.patron_saint_name && (
                    <div className="text-xs text-color-300 italic truncate">
                        {item?.patron_saint_name}
                        {item?.patron_saint_feast_day && ` (${item?.patron_saint_feast_day})`}
                    </div>
                )}

                {/* Footer row — pushed to bottom */}
                <div className="mt-auto flex items-center justify-between pt-2 border-t border-color-50">
                    <span className="flex items-center gap-1 text-xs text-color-300">
                        <BsPeopleFill size={12} className="text-color-400"/>
                        <span className="font-semibold text-color-100">{formatNumber(item?.member_count)}</span>
                        {' '}{t('members').toLowerCase()}
                    </span>
                    {isJoined ? (
                        <span className="px-2 py-0.5 text-xs font-semibold text-green-700 bg-green-50 rounded-full border border-green-200">
                            {t('joined')}
                        </span>
                    ) : canJoin ? (
                        <span
                            role="presentation"
                            onClick={e => e.stopPropagation()}>
                            <JoinButton
                                item={item}
                                id={getId(item)}
                                type={getType(item)}
                                size="small"
                                style={{minHeight: 36}}
                                className="text-doc-xs rounded-full"
                            />
                        </span>
                    ) : null}
                </div>
            </div>
        </Link>
    )
}

const renderDepartment = ({t, renderContextMenu = NullAsDefault} = {}) => (item, index) => {
    return (
        <div
            key={item.id}
            className="px-3 py-2 background-50 hover:shadow-md rounded-xl hover:scale-2xs">
            <div className="mb-2 pb-2 border-b flex items-center gap-3">
                <FaUsers size={20} className="text-indigo-400"/>
                <div className="flex flex-col flex-1">
                    <Link to={getLinkToDetail(item)} className="text-color-000 no-underline">
                        <h2 className={classNames("font-bold text-base leading-snug items-baseline")}>
                            {item?.title}
                            {!!item?.active_as_member?.id && <PiUserCircleCheckFill className="ml-3 text-green-300"/>}
                        </h2>
                    </Link>
                    {item.group_title && (
                        <div className="text-xs text-indigo-400 italic leading-snug tracking-wider">
                            {item?.group_title}
                        </div>
                    )}
                </div>
            </div>
            <div className="flex items-center justify-between">
                <span className="text-xs text-color-300">
                    <span className="font-semibold text-color-100">{item?.member_count}</span>{' '}{t('members').toLowerCase()}
                </span>
                {!!item?.available_actions?.can_join && !!!item?.joined_status && (
                    <JoinButton
                        item={item}
                        id={getId(item)}
                        type={getType(item)}
                        size="small"
                        className="text-doc-xs rounded-full"
                    />
                )}
            </div>
        </div>
    )
}

const OrganizationGroups = () => {
    const t = useTranslate()
    const {organization: item} = useContext(OrganizationContainerContext)
    const {isLg} = useContext(LayoutContext)
    const owner_id = getId(item)
    const owner_type = getType(item)
    const history = useHistory()

    return (
        <CustomLazyPaginationWrapper
            auto={false}
            sample={2}
            apiInfo={organization_getGroups_Api}
            renderItem={renderDepartment({t})}
            renderLoading={renderPagingLoading}
            id={[organization_getGroups_Api.path, item?.id].join("/")}
            query={bindQueryParams([{id: owner_id}])}
            renderWrap={(children, __, count) => {
                return !!count && (
                    <WrapperBlockSider
                        title="groups / ministries" titleColor="text-indigo-700" expanded={!isLg}
                        borderLeft={isLg}
                        borderTop={!isLg}
                        icon={<FaUsers size={18} className="text-indigo-400"/>}>
                        <div className="flex flex-col gap-4">
                            {children}
                        </div>
                    </WrapperBlockSider>
                )
            }}
            renderLoadMore={() => (
                <span
                    className="flex items-center justify-end gap-1 text-color-400 hover:text-color-100 cursor-pointer"
                    onClick={() => {
                        scrollToRow('contentOrg');
                        history.push(getLinkToDetail(item) + '/groups')
                    }}>
                    <span className="text-xs italic">
                        {t('see more')}
                    </span>
                    <FiChevronsRight size={13} className="my-auto"/>
                </span>
            )}
        />
    )
}

export const OrganizationGroupsGrid = () => {
    const t = useTranslate()
    const {organization: item} = useContext(OrganizationContainerContext)
    const owner_id = getId(item)

    const memberStats = _.get(item, 'workspace_settings.members.statistics', {})
    const rawTotalMembers = item?.total_members ?? memberStats?.member_count ?? 0
    const rawTotalDepts = memberStats?.department_count ?? 0
    const totalMembers = formatNumber(rawTotalMembers)
    const totalDepts = formatNumber(rawTotalDepts)
    const avgMembersPerDept = rawTotalDepts > 0 ? Math.round(rawTotalMembers / rawTotalDepts) : null

    const {response, isLoading} = useAsyncWithCache({
        cacheId: [organization_getGroupTree_Api.path, owner_id].join('/'),
        apiInfo: organization_getGroupTree_Api,
        query: {id: owner_id},
    })

    const {groupedDepts, ungrouped} = useMemo(() => {
        const list = _.get(response, 'data') || []
        const groupMap = {}
        const noGroup = []
        list.forEach(dept => {
            const g = dept.group_title
            if (g) {
                if (!groupMap[g]) groupMap[g] = []
                groupMap[g].push(dept)
            } else {
                noGroup.push(dept)
            }
        })
        return {groupedDepts: groupMap, ungrouped: noGroup}
    }, [response])

    const isEmpty = !isLoading && _.isEmpty(groupedDepts) && ungrouped.length === 0

    const renderGroup = (groupTitle, depts) => (
        <div key={groupTitle || 'ungrouped'} className="flex flex-col gap-3">
            {groupTitle && (
                <HeaderWithIconLine
                    renderTitle={() => (
                        <span className="font-semibold text-sm text-color-200 uppercase tracking-wider">
                            {groupTitle}
                        </span>
                    )}
                />
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {depts.map(deptItem => <DeptCard key={deptItem.id} item={deptItem} t={t}/>)}
            </div>
        </div>
    )

    return (
        <div className="flex flex-col gap-6 pb-8">
            {/* Header */}
            <HeaderWithIconLine
                icon={<FaUsers size={20} className="text-indigo-500"/>}
                title="groups / ministries"
                className="text-lg font-bold text-color-000"
                renderTitle={() => (
                    <div className="flex flex-col gap-0.5 flex-1">
                        <span className="font-bold text-base text-color-000 uppercase tracking-wide">
                            {t('groups / ministries')}
                        </span>
                    </div>
                )}
                renderSubTitle={() => (
                    <div className="flex flex-col gap-0.5 flex-1">
                        <span className="text-xs text-color-300 tracking-wide font-normal normal-case">
                            {t('groups / ministries description')}
                        </span>
                    </div>
                )}
                line={false}
            />

            {/* Stats row */}
            <div className="flex flex-col sm:flex-row gap-3">
                <StatCard
                    icon={<BsPeopleFill/>}
                    label={t('total members')}
                    value={totalMembers}
                    color="text-indigo-600"
                    bg="bg-indigo-50"
                />
                <StatCard
                    icon={<FaUsers/>}
                    label={t('groups / ministries')}
                    value={totalDepts}
                    note={avgMembersPerDept ? `≈ ${avgMembersPerDept} ${t('members per group')}` : null}
                    color="text-blue-600"
                    bg="bg-blue-50"
                />
            </div>

            {/* Groups by group_title */}
            {isLoading ? (
                <DeptSkeleton/>
            ) : isEmpty ? (
                <EmptyHolder
                    title={t('No data available')}
                    subTitle={t('groups / ministries description')}
                />
            ) : (
                <>
                    {Object.entries(groupedDepts).map(([groupTitle, depts]) =>
                        renderGroup(groupTitle, depts)
                    )}
                    {ungrouped.length > 0 && renderGroup(
                        !_.isEmpty(groupedDepts) ? t('list of group / ministry') : null,
                        ungrouped
                    )}
                </>
            )}
        </div>
    )
}

export default OrganizationGroups
